import QRCode from 'qrcode';
import { DEFAULT_RESTAURANT_IMAGE } from './constants';

type GenerateQROptions = {
  size?: number;
};

type DownloadImageQROptions = {
  reservationNo: string;
  restaurantName?: string;
  fileName?: string;
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const drawCenteredText = (
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
) => {
  context.textAlign = 'center';
  context.fillText(text, x, y);
};

const wrapText = (
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
) => {
  const words = `${text}`.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let currentLine = '';

  words.forEach(word => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;
    if (context.measureText(nextLine).width <= maxWidth || !currentLine) {
      currentLine = nextLine;
      return;
    }

    lines.push(currentLine);
    currentLine = word;
  });

  if (currentLine) lines.push(currentLine);

  return lines;
};

const drawRightWrappedText = (
  context: CanvasRenderingContext2D,
  text: string,
  right: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number,
) => {
  const wrappedLines = wrapText(context, text, maxWidth);
  const visibleLines = wrappedLines.slice(0, maxLines);

  visibleLines.forEach((line, index) => {
    const displayLine =
      index === visibleLines.length - 1 && wrappedLines.length > maxLines
        ? `${line.replace(/\.*$/, '')}...`
        : line;
    context.fillText(displayLine, right, y + index * lineHeight);
  });
};

const drawRoundedRect = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius,
    y + height,
  );
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
};

export const generateQR = (
  reservationNo: string,
  options?: GenerateQROptions,
) => {
  const value = `${reservationNo ?? ''}`.trim();
  if (!value) return Promise.reject(new Error('reservationNo is required'));

  return QRCode.toDataURL(value, {
    width: options?.size ?? 540,
    margin: 0,
    errorCorrectionLevel: 'H',
    color: {
      dark: '#000000',
      light: '#ffffff',
    },
  });
};

export const downloadImage = (dataUrl: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const downloadImageQR = async ({
  reservationNo,
  restaurantName = '',
  fileName,
}: DownloadImageQROptions) => {
  const qrDataUrl = await generateQR(reservationNo);
  const [logoImage, qrImage] = await Promise.all([
    loadImage(DEFAULT_RESTAURANT_IMAGE),
    loadImage(qrDataUrl),
  ]);
  const canvas = document.createElement('canvas');
  const width = 860;
  const height = 940;
  const context = canvas.getContext('2d');

  if (!context) throw new Error('Cannot create image context');

  canvas.width = width;
  canvas.height = height;

  context.fillStyle = '#f3f4f7';
  context.fillRect(0, 0, width, height);

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, 160);

  context.drawImage(logoImage, 48, 44, 300, 72);

  context.fillStyle = '#000000';
  context.font = '26px Arial, sans-serif';
  context.textAlign = 'right';
  context.fillText('Nhà hàng', width - 56, 70);
  context.font = 'bold 24px Arial, sans-serif';
  drawRightWrappedText(
    context,
    restaurantName || '-',
    width - 56,
    100,
    320,
    28,
    2,
  );

  context.fillStyle = '#e93624';
  context.fillRect(0, 160, width, 78);
  context.fillStyle = '#ffffff';
  context.font = 'bold 38px Arial, sans-serif';
  drawCenteredText(context, 'ĐẶT BÀN NHÀ HÀNG', width / 2, 212);

  context.fillStyle = '#ffffff';
  drawRoundedRect(context, 150, 270, 560, 560, 8);
  context.fill();
  context.drawImage(qrImage, 178, 298, 504, 504);

  context.fillStyle = '#6f6f6f';
  context.font = 'bold 30px Arial, sans-serif';
  drawCenteredText(context, `Mã đặt bàn: ${reservationNo}`, width / 2, 895);

  const dataUrl = canvas.toDataURL('image/png');
  downloadImage(dataUrl, fileName ?? `qr-${reservationNo}.png`);

  return dataUrl;
};
